import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import {
  DataGridPro,
  GridActionsCellItem,
  GridColDef,
  GridCsvExportOptions,
  GridRowParams,
  GridRowSelectionModel,
  getGridDateOperators,
  getGridNumericOperators,
  getGridStringOperators,
} from "@mui/x-data-grid-pro";
import { GridFilterItem, GridToolbarProps } from "@mui/x-data-grid";
import {
  IOrganisationResponseItem,
  IQuoteResponseItem,
  QuoteStatus,
  langauge,
  quoteEventLanguageMap,
  viewableQuoteStatuses,
  getAccountingSystemDeepLink,
} from "shared";
import { DateTime } from "luxon";
import {
  DocumentArrowUpIcon,
  DocumentCheckIcon,
  DocumentIcon,
  DocumentMinusIcon,
  FireIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { DocumentArrowDownIcon } from "@heroicons/react/20/solid";
import { Tooltip } from "@mui/material";
import { toast } from "react-toastify";
import { When } from "react-if";

import { useQuotes, useQuoteTableModels } from "../../hooks/useQuotes";
import { DatePopover } from "../../../common/Components/DatePopover";
import { useGetSelectedOrganisation } from "../../hooks/useGetSelectedOrganisation";
import { WrappedLink } from "../WrappedLink";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { useAccountingSystemTerm } from "../../hooks/useAccountingSystemTerm";
import { MultiButtonCardHeader } from "../../../common/Atoms/MultiButtonCardHeader";
import { env } from "../../../common/lib/env";
import { ITimeLineFeedItem, TimelineFeed } from "../../../common/Atoms/TimelineFeed";
import { useSyncQuotesMutation } from "../../services/api/quotesApi/quotes";
import { ITab, Tabs } from "../../../common/Atoms/Tabs";
import { getQuoteIcon } from "../../../common/lib/helpers";
import { useApiRequest } from "../../hooks/useApiRequest";
import { commonConfig, onRowSelectionChangeWrapper } from "../../lib/datagrid";
import { CustomGenericToolbar } from "../Common/Datagrid/CustomGenericToolbar";
import { DatagridCard } from "../Common/Datagrid/DatagridCard";

import { CustomDateFilterInput } from "./CustomDateFilterInput";

type ToolbarProps = GridToolbarProps & {
  onRemoveFilter: (filterId: GridFilterItem[`id`]) => void;
  rowSelectionModel: GridRowSelectionModel;
  currentOrg: IOrganisationResponseItem;
};

const dateFilterOperators = getGridDateOperators()
  .filter(op => [`onOrAfter`, `onOrBefore`].includes(op.value))
  .map(op => ({
    ...op,
    InputComponent: CustomDateFilterInput,
  }));

const stringContainsOperators = getGridStringOperators().filter(op => op.value === `contains`);

const numericOperators = getGridNumericOperators().filter(op => [`>=`, `=`, `<=`].includes(op.value));

function loadingReducer(state: Record<string, boolean>, action: Record<string, boolean>) {
  return {
    ...state,
    ...action,
  };
}

export function QuotesRoot() {
  const currentOrg = useGetSelectedOrganisation();
  const quoteTerm = useAccountingSystemTerm(`Quotes`);
  const [syncQuotes, { isLoading: syncQuotesLoading }] = useSyncQuotesMutation();
  const request = useApiRequest();
  const [currentTab, setCurrentTab] = useState<QuoteStatus>(QuoteStatus.SENT);
  const [downloadingPOs, setDownloadingPOs] = useReducer(loadingReducer, {});
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    type: `include`,
    ids: new Set(),
  });

  const {
    quotes,
    count,
    statusCounts,
    isLoading,
    isFetching,
  } = useQuotes(currentTab);

  const {
    filterModel,
    sortModel,
    paginationModel,
    setFilterModel,
    setSortModel,
    setPaginationModel,
    resetModels,
  } = useQuoteTableModels();

  // Start from a clean slate whenever the table mounts
  useEffect(() => {
    resetModels();
  }, []);

  // Selection is scoped to the currently loaded page. Clear it whenever the
  // page / tab / filters / sort change so a CSV export can never reference
  // rows that aren't loaded (and so silently omit them).
  useEffect(() => {
    setRowSelectionModel({ type: `include`, ids: new Set() });
  }, [paginationModel, sortModel, filterModel, currentTab]);

  function onTabChange(status: QuoteStatus) {
    setCurrentTab(status);
    // Switching tabs runs a fresh server query, so jump back to the first page
    setPaginationModel({ ...paginationModel, page: 0 });
  }

  const onRemoveFilter = (filterId: GridFilterItem[`id`]) => {
    setFilterModel({
      ...filterModel,
      items: filterModel.items.filter(item => item.id !== filterId),
    });
  };

  const quotesLastSyncedText = (currentOrg?.quotesLastSyncedAgo !== undefined ? `Last synced ${currentOrg.quotesLastSyncedAgo} minute${currentOrg.quotesLastSyncedAgo === 1 ? `` : `s`} ago` : ``) + ` (updates every hour)`;

  function onPortalView(hash: string) {
    window.open(`${env.portalWebUrl}/q/${hash}`, `_blank`, `noopener noreferrer`);
  }

  function handleQuoteSync() {
    if (currentOrg?.id) {
      syncQuotes({ organisationId: currentOrg?.id });
    }
  }

  async function onDownloadPO(quote: IQuoteResponseItem) {

    setDownloadingPOs({
      [quote.id]: true,
    });

    try {
      const result = await request({
        method: `GET`,
        responseType: `blob`,
        url: `${env.baseApiUrl}quotes/${quote.id}/download-po`,
      });

      const url = window.URL.createObjectURL(new Blob([result.data]));
      const link = document.createElement(`a`);
      link.href = url;
      link.setAttribute(`download`, quote.customerDataPOFilename || `quote-PO-${quote.number}` );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    catch (e) {
      const responseObj = await e.response?.data?.text();
      toast.error(`${responseObj ? `${responseObj}` : `Could not download P.O.`}`);
    }

    setDownloadingPOs({
      [quote.id]: false,
    });

  }

  const getDetailPanelContent: (params: GridRowParams<IQuoteResponseItem>) => React.ReactNode = useCallback(params => {

    const feed: ITimeLineFeedItem[] = params.row.events?.map(e => ({
      id: e.id,
      description: quoteEventLanguageMap.timeline[e.type].description,
      timestamp: e.createdAt.toString(),
      icon: getQuoteIcon(e.type),
    })) || [];

    return (
      <div className={ `p-6` }>
        <TimelineFeed
          feed={ feed }
          timezone={ currentOrg?.validatedTimezone }
        />
      </div>
    );
  }, []);

  const columns: GridColDef<IQuoteResponseItem>[] = useMemo(() => {
    const cols: GridColDef<IQuoteResponseItem>[] = [
      {
        field: `issueDateString`,
        headerName: `Issue Date`,
        flex: 1,
        valueGetter: (value, row) => row.issueDateString,
        filterOperators: dateFilterOperators,
        renderCell: ({ row }) => {
          return <DatePopover
            date={ row.issueDateString }
            labelFormat={ `dd LLL yyyy` }
            organisationTimezone={ currentOrg?.validatedTimezone }
          />;
        },
      },
      {
        field: `number`,
        headerName: `Number`,
        flex: 1,
        filterOperators: stringContainsOperators,
        renderCell: ({ row }) => {
          return (
            <WrappedLink
              to={ `/quotes/${ row.id }` }
            >
              <Paragraph
                variant={ `link` }
                as={ `span` }
              >
                { row.number }
              </Paragraph>
            </WrappedLink>
          );
        },
      },
      {
        field: `expiryDateString`,
        headerName: `Expiry Date`,
        flex: 1,
        valueGetter: (value, row) => row.expiryDateString,
        filterOperators: dateFilterOperators,
        renderCell: ({ row }) => {
          if (!row.expiryDateString) return `-`;

          return <DatePopover
            date={ row.expiryDateString }
            labelFormat={ `dd LLL yyyy` }
            organisationTimezone={ currentOrg?.validatedTimezone }
          />;
        },
      },
      {
        field: `contactName`,
        headerName: `Contact`,
        flex: 1,
        filterOperators: stringContainsOperators,
        renderCell: ({ row }) => {
          return (
            <WrappedLink
              to={ `/contacts/${ row.contactId }` }
            >
              <Paragraph
                variant={ `link` }
                as={ `span` }
              >
                { row.contactName }
              </Paragraph>
            </WrappedLink>
          );
        },
      },
      {
        field: `totalAmountFormatted`,
        headerName: `Value`,
        flex: 1,
        type: `number`,
        valueGetter: (value, row)  => row.amountTotalCents,
        valueFormatter: (value, row) => row.totalAmountFormatted,
        filterOperators: numericOperators,
      },
      {
        field: `customerDataPONumber`,
        headerName: `P.O. No.`,
        flex: 1,
        filterOperators: stringContainsOperators,
      },
      {
        field: `completedActions`,
        headerName: `Completed Actions`,
        flex: 1,
        sortable: false,
        filterable: false,
        valueGetter: (value, row) => (row.completedActions || [])
          .map(a => {
            const label = langauge.action[a.type]?.label || a.type;

            if (!a.executedDate) return label;

            const executed = DateTime.fromISO(a.executedDate, { zone: currentOrg?.validatedTimezone });

            return executed.isValid ? `${label} (${executed.toFormat(`dd LLL yyyy`)})` : label;
          })
          .join(`; `),
      },
      {
        field: `customerDataPOUploadUrl`,
        headerName: `P.O. Document`,
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          if (!row.customerDataPOUploadUrl) return ``;

          return (
            <Tooltip title={ row.customerDataPOFilename }>
              <span className={ `flex justify-center items-center h-full` }>
                <Paragraph
                  variant={ `link` }
                  className={ `truncate` }
                >
                  <a
                    onClick={ e => {
                      if (downloadingPOs[row.id]) return;

                      e.preventDefault();
                      onDownloadPO(row);
                    } }
                  >
                    <DocumentArrowDownIcon className={ `h-5 w-5 text-blue-400 inline-block` } />
                    <When condition={ downloadingPOs[row.id] }>
                      { `Downloading...` }
                    </When>

                    <When condition={ !downloadingPOs[row.id] }>
                      { row.customerDataPOFilename ? row.customerDataPOFilename : `Download` }
                    </When>
                  </a>
                </Paragraph>
              </span>
            </Tooltip>
          );
        },
      },
      {
        field: `actions`,
        type: `actions`,
        getActions: ({ row }) => {
          const items = [
            <GridActionsCellItem
              key={ `${row.id}-portal-link` }
              onClick={ () => onPortalView(row.hash) }
              // icon={ null }
              label={ `View Customer Portal` }
              disabled={ !viewableQuoteStatuses.includes(row.status) }
              showInMenu
            />,
          ];

          const externalLink = getAccountingSystemDeepLink(currentOrg, `quote`, row.externalId);
          if (externalLink) {
            items.push(
              <GridActionsCellItem
                key={ `${row.id}-external-link` }
                onClick={ () => window.open(externalLink, `_blank`, `noopener noreferrer`) }
                // icon={ null }
                label={ `View in ${currentOrg?.accountingSystemName}` }
                showInMenu
              />,
            );
          }

          return items;
        },
      },
    ];

    return cols;
  }, [currentOrg, downloadingPOs]);

  const tabs: ITab[] = useMemo(() => {
    const tabConfig: { status: QuoteStatus; icon: ITab[`icon`] }[] = [
      { status: QuoteStatus.SENT, icon: DocumentArrowUpIcon },
      { status: QuoteStatus.ACCEPTED, icon: DocumentCheckIcon },
      { status: QuoteStatus.DECLINED, icon: DocumentMinusIcon },
      { status: QuoteStatus.DRAFT, icon: DocumentIcon },
      { status: QuoteStatus.CONVERTED, icon: FireIcon },
      { status: QuoteStatus.DELETED, icon: TrashIcon },
    ];

    return tabConfig.map(({ status, icon }) => ({
      name: langauge.quotes.status[status],
      onClick: () => onTabChange(status),
      icon,
      href: `#`,
      current: currentTab === status,
      badge: statusCounts[status] !== undefined ? {
        text: `${statusCounts[status]}`,
        color: `gray`,
      } : undefined,
    }));
  }, [statusCounts, currentTab]);

  return (
    <div>
      <MultiButtonCardHeader
        title={ quoteTerm }
        variant={ `plain` }
        noDivider
        secondaryDescription={ quotesLastSyncedText }
        buttons={ [{
          buttonText: `Sync Now`,
          onClick: handleQuoteSync,
          loading: syncQuotesLoading,
          disabled: syncQuotesLoading,
          color: `paidniceGreenOutline`,
        }] }
      />
      <Tabs
        tabs={ tabs }
        className={ `pb-3` }
      />
      <DatagridCard className={ `h-[650px]` }>
        <DataGridPro
          rows={ quotes }
          columns={ columns }
          loading={ isLoading || isFetching }
          getDetailPanelContent={ getDetailPanelContent }
          getDetailPanelHeight={ () => `auto` }
          sx={ { ...commonConfig.headerStyle, ...commonConfig.toolbarStyle } }
          initialState={ {
            columns: {
              columnVisibilityModel: {
                completedActions: false,
              },
            },
          } }
          showToolbar

          // Server-side data
          filterMode={ `server` }
          sortingMode={ `server` }
          paginationMode={ `server` }
          filterDebounceMs={ 300 }

          // Pagination
          pagination
          pageSizeOptions={ [10, 25, 50, 100] }
          rowCount={ count }
          paginationModel={ paginationModel }
          onPaginationModelChange={ setPaginationModel }

          // Filtering & sorting
          filterModel={ filterModel }
          onFilterModelChange={ setFilterModel }
          sortModel={ sortModel }
          onSortModelChange={ setSortModel }

          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={ rowSelectionModel }
          onRowSelectionModelChange={ model => onRowSelectionChangeWrapper(model, quotes.map(q => q.id), setRowSelectionModel) }
          slots={ {
            toolbar: CustomToolbar,
          } }
          slotProps={ {
            toolbar: {
              currentOrg,
              rowSelectionModel,
              onRemoveFilter,
            },
            pagination: {
              showFirstButton: true,
              showLastButton: true,
            },
          } }
        />
      </DatagridCard>
    </div>
  );
}

function CustomToolbar({ currentOrg, rowSelectionModel, onRemoveFilter }: ToolbarProps) {
  const csvOptions: GridCsvExportOptions = {
    fileName: `quote_export_${currentOrg?.legalName}`,
    getRowsToExport: rowSelectionModel.ids.size ? () => [...rowSelectionModel.ids] : undefined,
    fields: [
      `issueDateString`,
      `number`,
      `expiryDateString`,
      `contactName`,
      `totalAmountFormatted`,
      `customerDataPONumber`,
      `completedActions`,
    ],
  };

  return (
    <CustomGenericToolbar
      rowSelectionModel={ rowSelectionModel }
      csvOptions={ csvOptions }
      onRemoveFilter={ onRemoveFilter }
      context={ `quotes` }
    />
  );
}
